import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn, UserPlus, Shield } from "lucide-react";

export default function LoginPage() {
  const { login, adminLogin, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "/";

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    name: "", email: "", phone: "", password: "", dob: "", gender: "Male" as "Male" | "Female" | "Other",
    address: "", city: "", pincode: "",
  });
  const [adminForm, setAdminForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(loginForm.email, loginForm.password);
    setLoading(false);
    if (result.success) { toast.success("Welcome back!"); navigate(redirect); }
    else toast.error(result.error);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!registerForm.name || !registerForm.email || !registerForm.phone || !registerForm.password) {
      toast.error("Please fill all required fields"); return;
    }
    setLoading(true);
    const result = await register(registerForm);
    setLoading(false);
    if (result.success) { toast.success("Account created successfully!"); navigate(redirect); }
    else toast.error(result.error);
  };

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const result = await adminLogin(adminForm.email, adminForm.password);
    setLoading(false);
    if (result.success) { toast.success("Admin access granted"); navigate("/admin"); }
    else toast.error(result.error);
  };

  return (
    <div className="min-h-[calc(100vh-10rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <span className="text-primary-foreground font-bold text-lg">D+</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">Welcome to DiagnoPlus</h1>
          <p className="text-muted-foreground text-sm mt-1">Book tests, view reports, manage your health</p>
        </div>

        <Tabs defaultValue="login" className="bg-card border border-border rounded-2xl p-6 shadow-sm">
          <TabsList className="grid grid-cols-3 mb-6">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="register">Register</TabsTrigger>
            <TabsTrigger value="admin">Admin</TabsTrigger>
          </TabsList>

          {/* Login Tab */}
          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label>Email Address</Label>
                <Input className="mt-1" type="email" placeholder="you@example.com" value={loginForm.email} onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <Label>Password</Label>
                <div className="relative mt-1">
                  <Input type={showPass ? "text" : "password"} placeholder="••••••••" value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} />
                  <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Demo: use email "arjun@example.com" / password "patient123"</p>
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                <LogIn className="h-4 w-4 mr-2" />{loading ? "Logging in..." : "Login"}
              </Button>
            </form>
          </TabsContent>

          {/* Register Tab */}
          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Full Name *</Label><Input className="mt-1" placeholder="John Doe" value={registerForm.name} onChange={e => setRegisterForm(f => ({ ...f, name: e.target.value }))} /></div>
                <div><Label>Email *</Label><Input className="mt-1" type="email" placeholder="john@email.com" value={registerForm.email} onChange={e => setRegisterForm(f => ({ ...f, email: e.target.value }))} /></div>
                <div><Label>Phone *</Label><Input className="mt-1" placeholder="9876543210" value={registerForm.phone} onChange={e => setRegisterForm(f => ({ ...f, phone: e.target.value }))} /></div>
                <div><Label>Date of Birth</Label><Input className="mt-1" type="date" value={registerForm.dob} onChange={e => setRegisterForm(f => ({ ...f, dob: e.target.value }))} /></div>
                <div>
                  <Label>Gender</Label>
                  <select className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={registerForm.gender} onChange={e => setRegisterForm(f => ({ ...f, gender: e.target.value as "Male" | "Female" | "Other" }))}>
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
                <div className="col-span-2"><Label>Address</Label><Input className="mt-1" placeholder="House No, Street, Area" value={registerForm.address} onChange={e => setRegisterForm(f => ({ ...f, address: e.target.value }))} /></div>
                <div><Label>City</Label><Input className="mt-1" placeholder="Bengaluru" value={registerForm.city} onChange={e => setRegisterForm(f => ({ ...f, city: e.target.value }))} /></div>
                <div><Label>Pincode</Label><Input className="mt-1" placeholder="560001" value={registerForm.pincode} onChange={e => setRegisterForm(f => ({ ...f, pincode: e.target.value }))} /></div>
                <div className="col-span-2">
                  <Label>Password *</Label>
                  <div className="relative mt-1">
                    <Input type={showPass ? "text" : "password"} placeholder="Create a password" value={registerForm.password} onChange={e => setRegisterForm(f => ({ ...f, password: e.target.value }))} />
                    <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                <UserPlus className="h-4 w-4 mr-2" />{loading ? "Creating account..." : "Create Account"}
              </Button>
            </form>
          </TabsContent>

          {/* Admin Tab */}
          <TabsContent value="admin">
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4 text-primary shrink-0" />
                <span>Admin: admin@diagnoplus.com / admin123</span>
              </div>
              <div><Label>Admin Email</Label><Input className="mt-1" type="email" placeholder="admin@diagnoplus.com" value={adminForm.email} onChange={e => setAdminForm(f => ({ ...f, email: e.target.value }))} /></div>
              <div>
                <Label>Password</Label>
                <div className="relative mt-1">
                  <Input type={showPass ? "text" : "password"} placeholder="••••••••" value={adminForm.password} onChange={e => setAdminForm(f => ({ ...f, password: e.target.value }))} />
                  <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                <Shield className="h-4 w-4 mr-2" />{loading ? "Verifying..." : "Admin Login"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
